// ============================================================
// FloraIQ - Fluxo Operacional: definicoes metodologicas
// ============================================================
// Movido de src/mocks/operational.ts. Contem apenas a DEFINICAO do metodo
// (etapas, itens de checklist, regras de validacao, produtos e relatorios
// previstos). Nao contem andamento, responsaveis, datas, contagens nem
// percentuais: nao ha fonte real de execucao por etapa ainda.

export type OperationalStageTab =
  | "planning"
  | "survey"
  | "field"
  | "processing3d"
  | "ladm"
  | "validation"
  | "publication";

export interface OperationalStageDef {
  id: number;
  tab: OperationalStageTab;
  nome: string;
  icon: string;
  objetivo: string;
  /** Itens que a etapa deve cumprir (definicao, nao estado). */
  checklist: string[];
}

export const operationalStages: OperationalStageDef[] = [
  {
    id: 1,
    tab: "planning",
    nome: "Planejamento",
    icon: "ri-calendar-check-line",
    objetivo: "Preparar a ação de cadastramento: área de estudo, unidades espaciais, base cartográfica, critérios técnicos, equipe e cronograma.",
    checklist: [
      "Definir área de estudo",
      "Definir unidades espaciais",
      "Preparar base cartográfica",
      "Definir formulário de cadastro",
      "Definir critérios técnicos",
      "Organizar equipe",
      "Organizar equipamentos",
      "Definir padrão de codificação",
      "Definir cronograma",
      "Definir responsáveis",
      "Definir produtos esperados",
    ],
  },
  {
    id: 2,
    tab: "survey",
    nome: "Levantamento Base",
    icon: "ri-flight-takeoff-line",
    objetivo: "Obter a base de imagens e geometria da área: drone, ortomosaico, LiDAR, nuvem de pontos e pré-identificação de copas.",
    checklist: [
      "Levantamento com drone",
      "Geração de ortomosaico",
      "Levantamento com LiDAR",
      "Geração de nuvem de pontos",
      "Organização das camadas geográficas",
      "Pré-identificação de exemplares arbóreos",
      "Validação da área coberta",
      "Conferência do SRID",
      "Conferência da qualidade das imagens",
    ],
  },
  {
    id: 3,
    tab: "field",
    nome: "Cadastro de Campo",
    icon: "ri-plant-line",
    objetivo: "Cadastrar e vistoriar os exemplares arbóreos em campo, com localização, identificação, mensuração, fotos e sincronização do coletor.",
    checklist: [],
  },
  {
    id: 4,
    tab: "processing3d",
    nome: "Processamento 3D",
    icon: "ri-stack-line",
    objetivo: "Processar nuvens de pontos e vincular métricas 3D (altura, copa, interferências) aos exemplares cadastrados.",
    checklist: [],
  },
  {
    id: 5,
    tab: "ladm",
    nome: "Modelagem LADM",
    icon: "ri-node-tree",
    objetivo: "Vincular cada exemplar à unidade espacial, fonte, responsável, RRR, histórico, documentos e nuvem de pontos, conforme FloraIQ-LADM.",
    checklist: [
      "Exemplar vinculado à unidade espacial",
      "Exemplar com fonte de informação",
      "Exemplar com responsável",
      "Exemplar com RRR (responsabilidades, restrições e autorizações)",
      "Exemplar com histórico de eventos",
      "Exemplar com documentos vinculados",
      "Exemplar com nuvem de pontos vinculada",
    ],
  },
  {
    id: 6,
    tab: "validation",
    nome: "Validação",
    icon: "ri-check-double-line",
    objetivo: "Aplicar as regras de consistência do cadastro e corrigir as inconformidades antes da publicação.",
    checklist: [],
  },
  {
    id: 7,
    tab: "publication",
    nome: "Publicação e Gestão",
    icon: "ri-earth-line",
    objetivo: "Publicar mapas, painéis, relatórios e camadas para a gestão municipal e, se autorizado, o portal público.",
    checklist: [
      "Publicar mapa arbóreo interativo",
      "Gerar dashboard gerencial",
      "Gerar relatórios técnicos",
      "Criar camadas temáticas",
      "Criar painel de risco",
      "Criar painel de manejo",
      "Criar indicadores de gestão",
      "Disponibilizar dados para gestão municipal",
      "Publicar portal público (se autorizado)",
      "Exportar camadas GIS",
    ],
  },
];

export interface ValidationRuleDef {
  regra: string;
  severidade: "Crítica" | "Alta" | "Média";
}

/** Regras de validacao da etapa 6 (definicao; os resultados reais ficam em Qualidade). */
export const operationalValidationRules: ValidationRuleDef[] = [
  { regra: "Exemplar possui geometria", severidade: "Crítica" },
  { regra: "Exemplar vinculado à unidade espacial", severidade: "Alta" },
  { regra: "Exemplar possui data de cadastro", severidade: "Média" },
  { regra: "Alteração gera histórico", severidade: "Alta" },
  { regra: "Informação técnica possui fonte", severidade: "Crítica" },
  { regra: "Supressão possui autorização", severidade: "Crítica" },
  { regra: "Risco alto possui evidência fotográfica", severidade: "Crítica" },
  { regra: "Nuvem de pontos possui metadados", severidade: "Alta" },
  { regra: "Intervenção possui responsável", severidade: "Alta" },
  { regra: "Exemplar removido registrado no histórico", severidade: "Média" },
];

export const validationSeverityBadge: Record<ValidationRuleDef["severidade"], string> = {
  "Crítica": "bg-red-100 text-red-700",
  "Alta": "bg-orange-100 text-orange-700",
  "Média": "bg-yellow-100 text-yellow-700",
};

export interface OperationalProductDef {
  id: string;
  titulo: string;
  tipo: string;
  descricao: string;
  formato: string;
}

/** Produtos previstos ao fim da acao de cadastramento. */
export const operationalDeliverables: OperationalProductDef[] = [
  { id: "del-01", titulo: "Base Cadastral Arbórea Georreferenciada", tipo: "Dados GIS", descricao: "Shapefile + GeoJSON com todos os exemplares cadastrados, atributos completos e metadados", formato: "SHP/GeoJSON" },
  { id: "del-02", titulo: "Mapa dos Exemplares Cadastrados", tipo: "Mapa", descricao: "Mapa interativo web com localização dos exemplares, filtros por espécie, risco e manejo", formato: "Web Map" },
  { id: "del-03", titulo: "Mapa de Risco", tipo: "Mapa Temático", descricao: "Mapa categorizando exemplares por nível de risco", formato: "Web Map + PDF" },
  { id: "del-04", titulo: "Mapa de Poda", tipo: "Mapa Temático", descricao: "Mapa com recomendações de poda", formato: "Web Map" },
  { id: "del-05", titulo: "Mapa de Supressão", tipo: "Mapa Temático", descricao: "Mapa indicando exemplares com recomendação de supressão e respectivas autorizações", formato: "PDF" },
  { id: "del-06", titulo: "Mapa de Cobertura Vegetal", tipo: "Mapa Temático", descricao: "Mapa de cobertura vegetal urbana com classificação por densidade e tipo", formato: "Web Map + PDF" },
  { id: "del-07", titulo: "Mapa de Espécies", tipo: "Mapa Temático", descricao: "Mapa com distribuição das espécies arbóreas por região da cidade", formato: "Web Map" },
  { id: "del-08", titulo: "Relatório por Bairro", tipo: "Relatório", descricao: "Relatório com estatísticas arbóreas segmentadas por bairro", formato: "PDF" },
  { id: "del-09", titulo: "Relatório por Logradouro", tipo: "Relatório", descricao: "Relatório com análise da arborização por logradouro", formato: "PDF" },
  { id: "del-10", titulo: "Relatório de Responsabilidades", tipo: "Relatório", descricao: "Relatório consolidando responsabilidades, RRR e partes envolvidas", formato: "PDF" },
  { id: "del-11", titulo: "Relatório de Restrições Ambientais", tipo: "Relatório", descricao: "Restrições ambientais: APP, UC, espécies protegidas e áreas tombadas", formato: "PDF" },
  { id: "del-12", titulo: "Relatório de Histórico de Manejo", tipo: "Relatório", descricao: "Linha do tempo dos eventos de manejo registrados", formato: "PDF" },
  { id: "del-13", titulo: "Relatório Técnico com Fontes", tipo: "Relatório", descricao: "Relatório técnico com as fontes documentais e evidências", formato: "PDF" },
  { id: "del-14", titulo: "Dashboard Gerencial", tipo: "Dashboard", descricao: "Painel com indicadores, gráficos e mapas da ação", formato: "Web App" },
  { id: "del-15", titulo: "Banco de Imagens", tipo: "Acervo", descricao: "Imagens georreferenciadas dos exemplares cadastrados", formato: "JPG + Metadados" },
  { id: "del-16", titulo: "Nuvem de Pontos Processada", tipo: "Dados 3D", descricao: "Nuvem de pontos classificada (solo, vegetação, edificações)", formato: "LAS/LAZ" },
  { id: "del-17", titulo: "Modelo 3D da Arborização", tipo: "Modelo 3D", descricao: "Modelo 3D da arborização com alturas, copas e métricas extraídas", formato: "WebGL/3D Tiles" },
  { id: "del-18", titulo: "Plano Preliminar de Manejo", tipo: "Documento", descricao: "Recomendações de manejo priorizadas por risco e urgência", formato: "PDF" },
];

export interface OperationalReportDef {
  id: string;
  titulo: string;
  descricao: string;
  etapaRelacionada: string;
}

/** Relatorios operacionais previstos por etapa. */
export const operationalReportDefs: OperationalReportDef[] = [
  { id: "opr-01", titulo: "Plano Operacional da Ação", descricao: "Escopo, cronograma, equipe, equipamentos e metodologia da ação", etapaRelacionada: "Planejamento" },
  { id: "opr-02", titulo: "Relatório de Planejamento", descricao: "Área, unidades espaciais, critérios técnicos e logística", etapaRelacionada: "Planejamento" },
  { id: "opr-03", titulo: "Relatório de Levantamento Base", descricao: "Resultados do drone, ortomosaico, LiDAR e pré-identificação", etapaRelacionada: "Levantamento Base" },
  { id: "opr-04", titulo: "Relatório de Produção de Campo", descricao: "Produtividade das equipes, exemplares cadastrados, fotos, vistorias e pendências", etapaRelacionada: "Cadastro de Campo" },
  { id: "opr-05", titulo: "Relatório de Processamento 3D", descricao: "Nuvens processadas, métricas extraídas, classificação e interferências", etapaRelacionada: "Processamento 3D" },
  { id: "opr-06", titulo: "Relatório de Modelagem LADM", descricao: "Completude da modelagem: unidades espaciais, fontes, RRR e histórico", etapaRelacionada: "Modelagem LADM" },
  { id: "opr-07", titulo: "Relatório de Validação", descricao: "Regras, conformidades, inconsistências e correções", etapaRelacionada: "Validação" },
  { id: "opr-08", titulo: "Relatório de Publicação", descricao: "Produtos publicados, mapas, painéis, portais e camadas", etapaRelacionada: "Publicação e Gestão" },
  { id: "opr-09", titulo: "Relatório Final da Ação", descricao: "Resumo executivo, resultados, indicadores e lições aprendidas", etapaRelacionada: "Publicação e Gestão" },
  { id: "opr-10", titulo: "Relatório de Indicadores de Gestão", descricao: "Indicadores para gestão municipal: cobertura arbórea, risco, manejo e evolução", etapaRelacionada: "Publicação e Gestão" },
];
